import { calculateComission } from "./calculateComission";
import { resolverFechaFactura } from "./comisionEdificiosUtil";

// comisionPorGeneracion — comisión comercial pendiente, con la fuente ÚNICA de la app
// (getSalesCommissionBreakdown para el monto, getCobroReal para "cobrado").
//
//   • Generado = Σ comisión del ejecutivo de las ventas (porción del ejecutivo del desglose; mismo
//     motor, NO se reimplementa — regla 10.1).
//   • Cobrado  = Σ comisión de esas ventas COBRADAS según `getCobroReal` (vía `cobroPorVenta`). SIN
//     cambiar la definición de "cobrado": factoring cuenta SOLO liquidado, nunca adelantado (regla
//     10.194 / 10.223 — confirmado por Ignacio).
//   • Pagado   = total ya pagado al ejecutivo (`pagadoTotal`; el llamador suma los haberes tipo
//     comisión ya disbursados = período de pago anterior al mes en curso).
//   • Pendiente = Cobrado − Pagado.
//
// Salida para la PROYECCIÓN: `pendientePorMesPago` = el pendiente distribuido por **mes de PAGO**
// (= mes de cobro real + 1, la misma convención que la materialización cobro+1). Lo ya pagado se
// imputa FIFO (del mes de pago más viejo al más nuevo), así lo que queda pendiente cae en los meses
// de pago futuros (ej.: factoring liquidado en julio → pago agosto; cobro de agosto → pago
// septiembre). Un mes de pago ya vencido y todavía impago lo agrupa el consumidor en el mes en curso.
// `totalPendiente = Σ pendientePorMesPago = totalCobrado − totalPagado` (se preserva el total).
//
// deps: { oportunidades, clientes, intermediarios, edificios, snapMap, pctMap, getRate, getTake,
//         cobroPorVenta, pagadoTotal }.

// Clave de venta igual que buildCobroPorVenta / comisionDelMes.
const keyVenta = (op) =>
  op.type === "contrato" ? `sales_contratos:${op.contrato_id}:${op.mes_index}` : `sales:${op.id}:`;

// Mes de generación "YYYY-MM" desde la fecha de factura (facturafecha, o fallback mes_cierre/ano-mes).
export const mesGeneracion = (op) => String(resolverFechaFactura(op)).slice(0, 7);

// Mes de PAGO = mes de cobro real + 1 (rolla el año en diciembre). "YYYY-MM" → "YYYY-MM".
// Sin fecha de cobro → sentinel "0000-00" (se ordena primero → se trata como vencido/mes en curso).
const mesPagoDeCobro = (fechaCobro) => {
  const ym = fechaCobro ? String(fechaCobro).slice(0, 7) : null;
  if (!ym || ym.length !== 7) return "0000-00";
  const [y, m] = ym.split("-").map(Number);
  if (!y || !m) return "0000-00";
  const pm = m === 12 ? 1 : m + 1;
  const py = m === 12 ? y + 1 : y;
  return `${py}-${String(pm).padStart(2, "0")}`;
};

// @returns {{ ejecutivoId, totalGenerado, totalCobrado, totalPagado, totalPendiente,
//             porBucket:[{mesGeneracion, generado, cobrado}], pendientePorMesPago: Map<"YYYY-MM", number> }}
export const comisionPorGeneracion = (ejecutivoId, deps = {}) => {
  const {
    oportunidades,
    clientes,
    intermediarios,
    edificios,
    snapMap,
    pctMap,
    getRate,
    getTake,
    cobroPorVenta,
    pagadoTotal = 0,
  } = deps;
  const ejec = String(ejecutivoId);
  const buckets = new Map(); // "YYYY-MM" generación -> { generado, cobrado } (para totales/reporte)
  const cobradoPorMesPago = new Map(); // "YYYY-MM" mes de PAGO (cobro+1) -> Σ comisión cobrada

  for (const op of oportunidades || []) {
    if (op.estatus !== "100%" || op.eliminada) continue;

    // Convertir a moneda local y resolver el take, luego el desglose existente (idéntico a
    // comisionDelMes / la card LIQUIDABLE — no re-descuenta nada, ya viene neto).
    const rate = getRate(op);
    const local = op.moneda_dolar ? { ...op, importe: (parseFloat(op.importe) || 0) * rate, moneda_dolar: false } : op;
    const desglose = calculateComission.getSalesCommissionBreakdown(
      local,
      clientes,
      intermediarios,
      edificios,
      undefined,
      snapMap,
      getTake(op),
      pctMap
    );
    const comision = desglose.filter((d) => String(d.ejecutivo) === ejec).reduce((s, d) => s + (d.comision || 0), 0);
    if (!comision) continue;

    const gm = mesGeneracion(op);
    if (!buckets.has(gm)) buckets.set(gm, { generado: 0, cobrado: 0 });
    const b = buckets.get(gm);
    b.generado += comision;

    // Cobrado = todo-o-nada por venta según getCobroReal (cobroPorVenta ya lo resolvió: MBF /
    // factoring LIQUIDADO / cuotas). Solo suma si la venta está 100% cobrada; se bucketea por mes de PAGO.
    const cobro = cobroPorVenta?.get(keyVenta(op));
    if (cobro?.cobrada) {
      b.cobrado += comision;
      const mp = mesPagoDeCobro(cobro.fecha);
      cobradoPorMesPago.set(mp, (cobradoPorMesPago.get(mp) || 0) + comision);
    }
  }

  const porBucket = [];
  let totalGenerado = 0;
  let totalCobrado = 0;
  for (const gm of [...buckets.keys()].sort()) {
    const b = buckets.get(gm);
    porBucket.push({ mesGeneracion: gm, generado: b.generado, cobrado: b.cobrado });
    totalGenerado += b.generado;
    totalCobrado += b.cobrado;
  }
  const totalPagado = Number(pagadoTotal) || 0;
  const totalPendiente = totalCobrado - totalPagado;

  // Imputación FIFO del total pagado a los meses de pago más viejos → el pendiente queda en los meses
  // de pago más nuevos. pendientePorMesPago solo lista los meses con pendiente > 0.
  const pendientePorMesPago = new Map();
  let restantePagado = totalPagado;
  for (const mp of [...cobradoPorMesPago.keys()].sort()) {
    const cob = cobradoPorMesPago.get(mp);
    const pagAqui = Math.min(Math.max(restantePagado, 0), cob);
    restantePagado -= pagAqui;
    const pend = cob - pagAqui;
    if (pend > 0) pendientePorMesPago.set(mp, pend);
  }

  return {
    ejecutivoId: ejec,
    totalGenerado,
    totalCobrado,
    totalPagado,
    totalPendiente,
    porBucket,
    pendientePorMesPago,
  };
};
